/**
 * Dispatches a capability field to the appropriate primitive based on
 * `field.type`. Unknown types render a fallback so the wizard doesn't crash
 * on a brand-new field shape — it just shows "unsupported" inline.
 *
 * Supported types:
 *   select          → SelectField           (single static choice)
 *   date_window     → SelectField           (a static-options select with date presets)
 *   async_select    → AsyncSelectField      (single-choice, options resolved via script/backend_tool)
 *   multi_select    → MultiSelectField      (checkboxes; supports async option providers)
 *   switch          → SwitchField           (boolean toggle)
 *   text            → TextField             (single-line free text — DataEQ extra_filter, etc.)
 *   number          → NumberField           (bounded integer — DataEQ record_limit, etc.)
 *
 * Further types plug in here without touching the calling step component.
 */
import React from 'react';

import type { CapabilityField } from '../../../../../types/importCapabilities';
import SelectField from './SelectField';
import AsyncSelectField from './AsyncSelectField';
import MultiSelectField from './MultiSelectField';
import SwitchField from './SwitchField';
import TextField from './TextField';
import NumberField from './NumberField';

interface FieldRendererProps {
  field: CapabilityField;
  value: unknown;
  onChange: (value: unknown) => void;
  /** Forwarded to MultiSelectField for async option resolution. */
  skillName: string;
  capabilityId: string;
  /** Partial source_config so far — forwarded as resolver context. */
  context: Record<string, unknown>;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  skillName,
  capabilityId,
  context,
}) => {
  switch (field.type) {
    case 'select':
    case 'date_window':
      return (
        <SelectField
          field={field}
          value={typeof value === 'string' ? value : undefined}
          onChange={onChange}
        />
      );

    case 'async_select':
      return (
        <AsyncSelectField
          field={field}
          value={typeof value === 'string' ? value : undefined}
          onChange={onChange}
          skillName={skillName}
          capabilityId={capabilityId}
          context={context}
        />
      );

    case 'multi_select':
      return (
        <MultiSelectField
          field={field}
          value={Array.isArray(value) ? (value as string[]) : undefined}
          onChange={onChange}
          skillName={skillName}
          capabilityId={capabilityId}
          context={context}
        />
      );

    case 'switch':
      return (
        <SwitchField
          field={field}
          value={typeof value === 'boolean' ? value : undefined}
          onChange={onChange}
        />
      );

    case 'text':
      return (
        <TextField
          field={field}
          value={typeof value === 'string' ? value : undefined}
          onChange={onChange}
        />
      );

    case 'number':
      return (
        <NumberField
          field={field}
          value={typeof value === 'number' ? value : undefined}
          onChange={onChange}
        />
      );

    default:
      return (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          Unsupported field type: <code className="font-mono">{field.type}</code> on
          field <code className="font-mono">{field.id}</code>. Update the wizard
          to render this primitive.
        </div>
      );
  }
};

export default FieldRenderer;
